/** Pure helpers for showing bot memory notes: readable titles from paths,
 * paths from typed names, sizes and dates in UK form. */
import type { ScopeSummary } from '../../../services/BotMemoryService';

export const NOTE_MAX_BYTES = 100 * 1024;

/** "/projects/naming-rules.md" -> folder "projects/", title "Naming rules". */
export function pathParts(path: string): { folder: string; title: string } {
  const trimmed = path.replace(/^\/+/, '');
  const slash = trimmed.lastIndexOf('/');
  const folder = slash >= 0 ? trimmed.slice(0, slash + 1) : '';
  const file = (slash >= 0 ? trimmed.slice(slash + 1) : trimmed).replace(/\.[a-z0-9]+$/i, '');
  const words = file.replace(/[-_]+/g, ' ').trim() || file;
  return { folder, title: words.charAt(0).toUpperCase() + words.slice(1) };
}

/** A typed name ("House style", "projects/Naming") becomes a path the server
 * accepts: lower-case, hyphens, ".md". Empty when nothing usable remains. */
export function nameToPath(name: string): string {
  const segments = name.trim().toLowerCase().replace(/\\/g, '/').split('/')
    .map((segment) => segment.replace(/\.md$/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
    .filter(Boolean);
  return segments.length ? `/${segments.join('/')}.md` : '';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function formatChanged(iso: string | null): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}

export const noteCount = (count: number) => `${count} ${count === 1 ? 'note' : 'notes'}`;

export function scopeLine(scope: ScopeSummary): string {
  return scope.count > 0 ? `${noteCount(scope.count)}, ${formatBytes(scope.bytes)}` : 'Nothing yet';
}

export const utf8Bytes = (text: string) => new TextEncoder().encode(text).length;

export const NOTES_FULL = 'Notes are full, so bots can read them but not add more until some are deleted.';
export const REDACTION_INCOMPLETE = 'Note removed. Its history could not be fully erased yet.';
